import { useState, useEffect, useContext } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { AuthContext } from '@/context/auth.context';

export type Theme = 'light' | 'dark' | 'system';
export type FontSize = 'small' | 'medium' | 'large';

export interface Preferences {
  theme: Theme;
  fontSize: FontSize;
  notificationsEnabled: boolean;
  // Deadline notification channels
  deadlineNotificationsApp: boolean;
  deadlineNotificationsEmail: boolean;
  deadlineNotificationsTelegram: boolean;
  telegramChatId: string | null;
  autonomousCommunity: string;
}

export function usePreferences() {
  const authContext = useContext(AuthContext);
  const user = authContext?.user ?? null;
  const [preferences, setPreferences] = useState<Preferences>({
    theme: 'system',
    fontSize: 'medium',
    notificationsEnabled: true,
    deadlineNotificationsApp: true,
    deadlineNotificationsEmail: true,
    deadlineNotificationsTelegram: false,
    telegramChatId: null,
    autonomousCommunity: 'otra',
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadPreferences();
    }
  }, [user]);

  useEffect(() => {
    applyTheme(preferences.theme);
    applyFontSize(preferences.fontSize);
  }, [preferences.theme, preferences.fontSize]);

  const loadPreferences = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('theme, font_size, notifications_enabled, deadline_notifications_app, deadline_notifications_email, deadline_notifications_telegram, telegram_chat_id, autonomous_community')
        .eq('id', user.id)
        .single();

      if (error) throw error;

      if (data) {
        setPreferences({
          theme: (data.theme as Theme) || 'system',
          fontSize: (data.font_size as FontSize) || 'medium',
          notificationsEnabled: data.notifications_enabled ?? true,
          deadlineNotificationsApp: data.deadline_notifications_app ?? true,
          deadlineNotificationsEmail: data.deadline_notifications_email ?? true,
          deadlineNotificationsTelegram: data.deadline_notifications_telegram ?? false,
          telegramChatId: data.telegram_chat_id || null,
          autonomousCommunity: data.autonomous_community || 'otra',
        });
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updatePreferences = async (newPreferences: Partial<Preferences>) => {
    if (!user) return;

    const updatedPreferences = { ...preferences, ...newPreferences };
    setPreferences(updatedPreferences);

    try {
      const updateData: Record<string, any> = {};
      
      if (newPreferences.theme !== undefined) {
        updateData.theme = newPreferences.theme;
      }
      if (newPreferences.fontSize !== undefined) {
        updateData.font_size = newPreferences.fontSize;
      }
      if (newPreferences.notificationsEnabled !== undefined) {
        updateData.notifications_enabled = newPreferences.notificationsEnabled;
      }
      if (newPreferences.deadlineNotificationsApp !== undefined) {
        updateData.deadline_notifications_app = newPreferences.deadlineNotificationsApp;
      }
      if (newPreferences.deadlineNotificationsEmail !== undefined) {
        updateData.deadline_notifications_email = newPreferences.deadlineNotificationsEmail;
      }
      if (newPreferences.deadlineNotificationsTelegram !== undefined) {
        updateData.deadline_notifications_telegram = newPreferences.deadlineNotificationsTelegram;
      }
      if (newPreferences.autonomousCommunity !== undefined) {
        updateData.autonomous_community = newPreferences.autonomousCommunity;
      }

      const { error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', user.id);

      if (error) throw error;
    } catch (error) {
      console.error('Error updating preferences:', error);
      // Revert on error
      loadPreferences();
    }
  };

  const applyTheme = (theme: Theme) => {
    const root = window.document.documentElement;
    
    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
      root.classList.toggle('dark', systemTheme === 'dark');
    } else {
      root.classList.toggle('dark', theme === 'dark');
    }
  };

  const applyFontSize = (fontSize: FontSize) => {
    const root = window.document.documentElement;
    
    // Remove existing font size classes
    root.classList.remove('font-small', 'font-medium', 'font-large');
    
    // Add new font size class
    root.classList.add(`font-${fontSize}`);
  };

  return {
    preferences,
    updatePreferences,
    isLoading,
    reloadPreferences: loadPreferences,
  };
}
